import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { AUTH_MODE, useAuth } from "@/lib/auth";

export const Route = createFileRoute("/auth/callback")({
  head: () => ({ meta: [{ title: "Signing you in… — Dravian Horizon" }] }),
  component: AuthCallback,
});

/**
 * Lands here after the Cognito Hosted UI redirects with `?code=…`.
 * react-oidc-context's <AuthProvider> intercepts the code+state, exchanges
 * them for tokens (PKCE), and stores the user. We just wait for it.
 */
function AuthCallback() {
  const auth = useAuth();
  const qc = useQueryClient();

  // After a successful sign-in, react-oidc-context cleans the URL itself,
  // but the route doesn't reload — push the user to HQ explicitly. We also
  // bust the cached /auth/me so the route guard re-fetches with the new token.
  useEffect(() => {
    if (auth.isAuthenticated) {
      qc.invalidateQueries({ queryKey: ["me"] });
      window.history.replaceState(null, "", "/hq");
    }
  }, [auth.isAuthenticated, qc]);

  if (AUTH_MODE !== "cognito") {
    return <Navigate to="/" />;
  }

  if (auth.error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-[20px] font-semibold">Sign-in failed</h1>
          <p className="text-[13px] text-risk-high">{auth.error}</p>
          <a
            href="/login"
            className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  if (auth.isAuthenticated) {
    return <Navigate to="/hq" />;
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4">
      <div className="text-center space-y-2">
        <div className="text-[13px] text-muted-foreground">Signing you in…</div>
      </div>
    </div>
  );
}
